import React from 'react';
import {
  MessageSquare,
  CheckCircle2,
  PieChart,
  StickyNote,
  ExternalLink,
  Search,
  History,
  Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { UTILITY_PANELS } from '../../hooks/useWorkflowUtilityPanels';

const RAIL_ITEMS = [
  {
    id: UTILITY_PANELS.comments,
    label: 'Comments',
    shortcut: null,
    icon: MessageSquare,
    accent: false,
  },
  {
    id: UTILITY_PANELS.validation,
    label: 'Workflow checklist',
    shortcut: null,
    icon: CheckCircle2,
    accent: false,
  },
  {
    id: UTILITY_PANELS.stats,
    label: 'Stats view',
    shortcut: ['⌘', '⇧', 'S'],
    icon: PieChart,
    accent: false,
  },
  {
    id: UTILITY_PANELS.sticky,
    label: 'Sticky notes',
    shortcut: ['⇧', 'S'],
    icon: StickyNote,
    accent: false,
  },
  {
    id: UTILITY_PANELS.switcher,
    label: 'Workflow Switcher',
    shortcut: ['⇧', 'W'],
    icon: ExternalLink,
    accent: false,
  },
  {
    id: UTILITY_PANELS.search,
    label: 'Search workflow',
    shortcut: null,
    icon: Search,
    accent: false,
  },
  {
    id: UTILITY_PANELS.versions,
    label: 'Version history',
    shortcut: null,
    icon: History,
    accent: false,
  },
  {
    id: UTILITY_PANELS.ai,
    label: 'Workflow AI',
    shortcut: ['⇧', 'A'],
    icon: Sparkles,
    accent: true,
  },
];

function ShortcutKeys({ keys }) {
  if (!keys?.length) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-0.5">
      {keys.map((key) => (
        <kbd
          key={key}
          className="inline-flex min-w-[18px] items-center justify-center rounded border border-white/30 bg-white/10 px-1 text-[10px] font-medium"
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}

const WorkflowUtilityRail = ({
  activePanel,
  statsViewActive,
  stickyPaletteOpen,
  onToggle,
}) => (
  <div
    className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-zinc-700/60 bg-zinc-900/50 py-3"
    data-testid="workflow-utility-rail"
  >
    {RAIL_ITEMS.map((item) => {
      const Icon = item.icon;
      const isActive =
        item.id === UTILITY_PANELS.stats
          ? statsViewActive
          : item.id === UTILITY_PANELS.sticky
            ? stickyPaletteOpen
            : activePanel === item.id;

      return (
        <div key={item.id} className="group relative">
          <button
            type="button"
            onClick={() => onToggle(item.id)}
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              isActive
                ? 'border-sky-200 bg-sky-50 text-slate-900'
                : 'border-transparent text-zinc-400 hover:bg-zinc-800 hover:text-white',
              item.accent && !isActive && 'text-violet-400 hover:text-violet-300',
            )}
            data-testid={`utility-rail-${item.id}`}
            aria-label={item.label}
            aria-pressed={isActive}
          >
            <Icon size={18} className={item.accent && !isActive ? 'text-violet-400' : undefined} />
          </button>
          <div
            role="tooltip"
            className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-black px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block"
          >
            {item.label}
            <ShortcutKeys keys={item.shortcut} />
          </div>
        </div>
      );
    })}
  </div>
);

export default WorkflowUtilityRail;
